<template>
    <div class="notices-page" :class="{ dark: dark }">
        <div class="notices-container">
            <section class="notices-panel" :aria-busy="loading ? 'true' : 'false'">
                <header class="notices-header">
                    <div>
                        <h1 class="notices-title">{{ $t('student.notifications.pageTitle') }}</h1>
                        <p class="notices-subtitle">{{ $t('student.notifications.subtitle') }}</p>
                    </div>
                    <button
                        type="button"
                        class="mark-all-read"
                        :disabled="markingAllAsRead || unreadCount === 0"
                        @click="markAllAsRead"
                    >
                        <span v-if="markingAllAsRead" class="mark-all-read__spinner" aria-hidden="true"></span>
                        <v-icon v-else size="18">mdi-check</v-icon>
                        {{ $t('student.notifications.markAllRead') }}
                    </button>
                </header>

                <div v-if="loading" class="notices-skeleton-filters" aria-hidden="true">
                    <span class="notices-skeleton-bar"></span>
                    <span class="notices-skeleton-bar"></span>
                    <span class="notices-skeleton-bar notices-skeleton-bar--short"></span>
                </div>
                <div v-else class="notices-filters">
                    <label class="notices-search">
                        <v-icon class="notices-search-icon" size="19">mdi-magnify</v-icon>
                        <input
                            v-model="search"
                            type="search"
                            :placeholder="$t('student.notifications.searchPlaceholder')"
                            :aria-label="$t('student.notifications.searchPlaceholder')"
                        />
                    </label>
                    <div class="notices-filter-field">
                        <span class="notices-filter-label">{{ $t('student.notifications.statusLabel') }}</span>
                        <select v-model="status" class="notices-filter-control" :aria-label="$t('student.notifications.statusLabel')">
                            <option value="all">{{ $t('student.notifications.statusAll') }}</option>
                            <option value="unread">{{ $t('student.notifications.statusUnread') }}</option>
                            <option value="read">{{ $t('student.notifications.statusRead') }}</option>
                        </select>
                    </div>
                    <button
                        type="button"
                        class="notices-clear"
                        :disabled="!hasActiveFilters"
                        @click="clearFilters"
                    >
                        <v-icon size="18">mdi-filter-off-outline</v-icon>
                        {{ $t('student.notifications.clearFilters') }}
                    </button>
                </div>

                <div v-if="loading" class="notices-skeletons" :aria-label="$t('student.notifications.loading')">
                    <span v-for="n in 4" :key="n" class="notices-skeleton-card"></span>
                </div>

                <div v-else-if="paged.length === 0" class="empty-notices">
                    <span class="empty-notices__icon" aria-hidden="true">
                        <v-icon>mdi-bell-outline</v-icon>
                    </span>
                    <h2>{{ emptyTitle }}</h2>
                    <p>{{ emptyHint }}</p>
                    <button
                        v-if="hasActiveFilters"
                        type="button"
                        class="notices-clear"
                        @click="clearFilters"
                    >
                        <v-icon size="18">mdi-filter-off-outline</v-icon>
                        {{ $t('student.notifications.clearFilters') }}
                    </button>
                </div>

                <ul v-else class="notices-list">
                    <li
                        v-for="item in paged"
                        :key="item.id"
                        class="notice-card"
                        :class="{
                            'notice-card--unread': isUnread(item),
                            'notice-card--expanded': expandedId === item.id
                        }"
                    >
                        <button
                            type="button"
                            class="notice-card-header"
                            :aria-expanded="expandedId === item.id ? 'true' : 'false'"
                            :aria-controls="'notice-body-' + item.id"
                            :aria-label="expandedId === item.id
                                ? $t('student.notifications.collapseNotice', { title: item.title })
                                : $t('student.notifications.expandNotice', { title: item.title })"
                            @click="toggle(item)"
                        >
                            <span class="notice-icon" aria-hidden="true">
                                <v-icon>mdi-bullhorn-outline</v-icon>
                            </span>
                            <span class="notice-main">
                                <span class="notice-heading">
                                    <h3>{{ item.title }}</h3>
                                    <span v-if="isUnread(item)" class="notice-new-badge">
                                        {{ $t('student.notifications.newBadge') }}
                                    </span>
                                </span>
                                <span v-if="preview(item)" class="notice-preview">{{ preview(item) }}</span>
                            </span>
                            <span class="notice-date">
                                <v-icon size="18">mdi-calendar-blank-outline</v-icon>
                                {{ formatDate(item.dateInsert) }}
                            </span>
                            <span
                                class="notice-read-state"
                                :class="{ 'notice-read-state--unread': isUnread(item) }"
                            >
                                {{ isUnread(item) ? $t('student.notifications.unread') : $t('student.notifications.read') }}
                            </span>
                            <span class="notice-chevron" aria-hidden="true">
                                <v-icon>{{ expandedId === item.id ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                            </span>
                        </button>

                        <div
                            v-show="expandedId === item.id"
                            :id="'notice-body-' + item.id"
                            class="notice-expanded-body"
                            role="region"
                        >
                            <div class="notice-expanded-content" v-html="item.description"></div>
                            <div v-if="isUnread(item)" class="notice-expanded-divider"></div>
                            <button
                                v-if="isUnread(item)"
                                type="button"
                                class="notice-mark-read"
                                :disabled="markingAsRead === item.id"
                                @click="markAsRead(item.id)"
                            >
                                <v-icon size="16">mdi-check</v-icon>
                                {{ $t('student.notifications.markRead') }}
                            </button>
                        </div>
                    </li>
                </ul>

                <footer v-if="!loading && filtered.length > 0" class="notices-pagination">
                    <p class="notices-pagination-summary">{{ pageSummary }}</p>
                    <div class="notices-pagination-controls">
                        <label class="notices-page-size-label" :for="'notices-page-size'">
                            {{ $t('student.notifications.perPage') }}
                        </label>
                        <select
                            id="notices-page-size"
                            v-model.number="pageSize"
                            class="notices-page-size"
                        >
                            <option v-for="size in pageSizeOptions" :key="size" :value="size">{{ size }}</option>
                        </select>
                        <div class="notices-page-nav">
                            <button
                                type="button"
                                class="notices-page-arrow"
                                :disabled="page <= 1"
                                :aria-label="$t('student.notifications.firstPage')"
                                @click="page = 1"
                            >
                                <v-icon size="18">mdi-chevron-double-left</v-icon>
                            </button>
                            <button
                                type="button"
                                class="notices-page-arrow"
                                :disabled="page <= 1"
                                :aria-label="$t('student.notifications.previousPage')"
                                @click="page = page - 1"
                            >
                                <v-icon size="18">mdi-chevron-left</v-icon>
                            </button>
                            <button
                                v-for="n in visiblePages"
                                :key="n"
                                type="button"
                                class="notices-page-number"
                                :class="{ 'notices-page-number--active': n === page }"
                                :aria-label="$t('student.notifications.pageNumber', { n })"
                                :aria-current="n === page ? 'page' : null"
                                @click="page = n"
                            >
                                {{ n }}
                            </button>
                            <button
                                type="button"
                                class="notices-page-arrow"
                                :disabled="page >= totalPages"
                                :aria-label="$t('student.notifications.nextPage')"
                                @click="page = page + 1"
                            >
                                <v-icon size="18">mdi-chevron-right</v-icon>
                            </button>
                        </div>
                    </div>
                </footer>
            </section>
        </div>
    </div>
</template>

<script>
import api from '@/utils/portal/notification.js'
import { mapState } from 'vuex';
import { toast } from 'vue3-toastify';
import './css/notices-portal.css';

export default {
    data() {
        return {
            data: [],
            loading: true,
            search: '',
            status: 'all',
            page: 1,
            pageSize: 10,
            pageSizeOptions: [10, 25, 50],
            pageWindow: 5,
            expandedId: null,
            markingAsRead: null,
            markingAllAsRead: false,
            readNotifications: new Set()
        }
    },
    computed: {
        ...mapState(['token', 'dark', 'user']),
        hasActiveFilters() {
            return this.search.trim() !== '' || this.status !== 'all';
        },
        unreadCount() {
            return this.data.filter((item) => this.isUnread(item)).length;
        },
        filtered() {
            const query = this.search.trim().toLowerCase();
            return this.data.filter((item) => {
                if (this.status === 'unread' && !this.isUnread(item)) return false;
                if (this.status === 'read' && this.isUnread(item)) return false;
                if (!query) return true;
                const title = String(item.title || '').toLowerCase();
                const body = this.plainText(item.description).toLowerCase();
                return title.includes(query) || body.includes(query);
            });
        },
        totalPages() {
            return Math.max(1, Math.ceil(this.filtered.length / this.pageSize));
        },
        paged() {
            const start = (this.page - 1) * this.pageSize;
            return this.filtered.slice(start, start + this.pageSize);
        },
        visiblePages() {
            const total = this.totalPages;
            const size = this.pageWindow;
            if (total <= size) {
                return Array.from({ length: total }, (_, index) => index + 1);
            }
            let start = Math.max(1, this.page - Math.floor(size / 2));
            let end = start + size - 1;
            if (end > total) {
                end = total;
                start = end - size + 1;
            }
            const pages = [];
            for (let n = start; n <= end; n += 1) pages.push(n);
            return pages;
        },
        pageSummary() {
            const total = this.filtered.length;
            const from = total === 0 ? 0 : (this.page - 1) * this.pageSize + 1;
            const to = Math.min(this.page * this.pageSize, total);
            const key = total === 1 ? 'student.notifications.showingOne' : 'student.notifications.showingMany';
            return this.$t(key, { from, to, total });
        },
        emptyTitle() {
            return this.hasActiveFilters
                ? this.$t('student.notifications.emptyFilteredTitle')
                : this.$t('student.notifications.emptyTitle');
        },
        emptyHint() {
            return this.hasActiveFilters
                ? this.$t('student.notifications.emptyFilteredHint')
                : this.$t('student.notifications.emptyHint');
        }
    },
    watch: {
        search() {
            this.page = 1;
        },
        status() {
            this.page = 1;
        },
        pageSize() {
            this.page = 1;
        },
        filtered() {
            if (this.page > this.totalPages) this.page = this.totalPages;
        }
    },
    methods: {
        isUnread(item) {
            return item?.isRead === 0;
        },
        plainText(value) {
            return String(value || '')
                .replace(/<[^>]*>/g, ' ')
                .replace(/&nbsp;/gi, ' ')
                .replace(/&amp;/gi, '&')
                .replace(/&lt;/gi, '<')
                .replace(/&gt;/gi, '>')
                .replace(/\s+/g, ' ')
                .trim();
        },
        preview(item) {
            return this.plainText(item?.description);
        },
        clearFilters() {
            this.search = '';
            this.status = 'all';
        },
        toggle(item) {
            if (!item) return;
            if (this.expandedId === item.id) {
                this.expandedId = null;
                return;
            }
            this.expandedId = item.id;
            if (item.id && item.isRead === 0) {
                this.markAsRead(item.id);
            }
        },
        updatePageWindow() {
            this.pageWindow = window.innerWidth <= 480 ? 3 : 5;
        },
        async getDataNotification() {
            this.loading = true;
            try {
                const response = await api.getDataNotifications(this.token);
                const list = Array.isArray(response) ? response : [];
                const filteredNotifications = list.filter((notification) => notification.status === 1);
                this.data = filteredNotifications;
                filteredNotifications.forEach((notification) => {
                    if (notification.isRead === 1) {
                        this.readNotifications.add(notification.id);
                    }
                });
            } catch (error) {
                toast.error(this.$t('student.notifications.toastLoadError'));
                console.error('Erro na listagem da notificações');
            } finally {
                this.loading = false;
            }
        },
        async readNotification(id) {
            try {
                const payload = {
                    idNotification: id,
                    idUser: this.user?.id || null,
                };
                await api.readNotification(this.token, payload);
                this.readNotifications.add(id);
                return true;
            } catch (error) {
                console.error('Erro ao marcar notificação como lida:', error);
                throw error;
            }
        },
        async markAsRead(id) {
            if (!id) return;
            const notification = this.data.find((item) => item.id === id);
            if (!notification || notification.isRead === 1) return;
            if (this.markingAsRead === id) return;

            this.markingAsRead = id;
            try {
                await this.readNotification(id);
                notification.isRead = 1;
                toast.success(this.$t('student.notifications.toastMarkedRead'));
                window.dispatchEvent(new CustomEvent('notification-read'));
            } catch (error) {
                toast.error(this.$t('student.notifications.toastMarkReadError'));
            } finally {
                this.markingAsRead = null;
            }
        },
        async markAllAsRead() {
            this.markingAllAsRead = true;
            try {
                const unreadNotifications = this.data.filter((notification) => notification.isRead === 0);
                if (unreadNotifications.length === 0) {
                    toast.info(this.$t('student.notifications.toastAllAlreadyRead'));
                    return;
                }
                const promises = unreadNotifications.map((notification) => this.readNotification(notification.id));
                await Promise.all(promises);
                unreadNotifications.forEach((notification) => {
                    notification.isRead = 1;
                });
                toast.success(this.$t('student.notifications.toastMarkedAllRead', { n: unreadNotifications.length }));
                window.dispatchEvent(new CustomEvent('notification-read'));
            } catch (error) {
                toast.error(this.$t('student.notifications.toastMarkAllError'));
            } finally {
                this.markingAllAsRead = false;
            }
        },
        formatDate(dateString) {
            if (!dateString || dateString === null) return this.$t('student.dates.unavailable');
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                return this.$t('student.dates.invalid');
            }
            const now = new Date();
            const diffInMs = now - date;
            const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
            const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
            const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
            if (diffInMinutes < 1) {
                return this.$t('student.dates.justNow');
            } else if (diffInMinutes < 60) {
                return this.$t('student.dates.minutesAgo', { n: diffInMinutes });
            } else if (diffInHours < 24) {
                return this.$t('student.dates.hoursAgo', { n: diffInHours });
            } else if (diffInDays === 1) {
                return this.$t('student.dates.yesterday');
            } else if (diffInDays < 7) {
                return this.$t('student.dates.daysAgo', { n: diffInDays });
            }
            return date.toLocaleDateString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        }
    },
    created() {
        this.getDataNotification();
    },
    mounted() {
        this.updatePageWindow();
        window.addEventListener('resize', this.updatePageWindow);
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.updatePageWindow);
    }
}
</script>
